import { BlockchainFiltersMoleculeReatom } from "@/app/blockchains/filters/blockchain-filters-molecule.reatom"
import { SellCollectionNftMoleculeReatom } from "@/app/collections/instant-sell/use-collection-nfts-sell.reatom"
import { NftSelectionMoleculeReatom } from "@/app/nft/sell/selection/nft-selection-molecule.reatom"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { Blockchain } from "@anaconda/core/blockchain/types"
import { useMolecule } from "bunshi/react"
import { useCallback, useMemo, useRef } from "react"
import { useSwitchEvmChain } from "../../use-switch-evm-chain"
import { AcceptCollectionBidTransactionMoleculeReatom } from "./evm-accept-collection-bid-molecule.reatom"

export function useEvmAcceptCollectionBid() {
  const { offerByNft } = useMolecule(SellCollectionNftMoleculeReatom)
  const { selectedNftsAtom } = useMolecule(NftSelectionMoleculeReatom)
  // Read from atom directly - this will be reactive when used inside reatomComponent
  const selectedNfts = selectedNftsAtom()
  const { acceptCollectionBidActionFactory } = useMolecule(
    AcceptCollectionBidTransactionMoleculeReatom,
  )
  const acceptCollectionBidAction = useMemo(() => {
    return acceptCollectionBidActionFactory()
  }, [acceptCollectionBidActionFactory])
  const { address } = useMolecule(WalletConnectMoleculeReatom)
  const { selectedBlockchain } = useMolecule(BlockchainFiltersMoleculeReatom)

  const addressValue = address()
  const selectedBlockchainValue = selectedBlockchain()

  const blockchain =
    selectedNfts.length > 0
      ? selectedNfts[0].collection.blockchain
      : selectedBlockchainValue === "ALL"
        ? null
        : selectedBlockchainValue

  // Capture current offer data at render time (before dialog closes)
  const currentOfferByNft = offerByNft()

  // Capture data in a ref so it survives dialog close
  // selectedNfts and currentOfferByNft are captured at the moment mutate() is called
  const capturedDataRef = useRef<{
    selectedNfts: typeof selectedNfts
    offerByNft: typeof currentOfferByNft
  } | null>(null)

  const onSuccess = useCallback(() => {
    if (!addressValue || !blockchain) {
      throw new Error("Cannot accept bid: no address or blockchain")
    }

    const captured = capturedDataRef.current
    if (!captured || captured.selectedNfts.length === 0) {
      throw new Error("No NFTs selected")
    }

    acceptCollectionBidAction({
      selectedNftsAtom,
      offerByNftAtom: offerByNft,
      blockchain,
      // Pass captured data for use after dialog closes
      capturedSelectedNfts: captured.selectedNfts,
      capturedOfferByNft: captured.offerByNft,
    })
  }, [
    acceptCollectionBidAction,
    selectedNftsAtom,
    offerByNft,
    blockchain,
    addressValue,
  ])

  // Don't use chain switch hook if blockchain is null
  const shouldUseChainSwitch = blockchain !== null
  const { action, switchInProgress } = useSwitchEvmChain<void>(
    blockchain ?? Blockchain.ETHEREUM, // Fallback to ETHEREUM if null (won't be used)
    onSuccess,
  )

  // Wrap action to capture current data before dialog closes
  const wrappedAction = useCallback(() => {
    // Capture current state at the moment of click (before dialog closes)
    capturedDataRef.current = {
      selectedNfts,
      offerByNft: currentOfferByNft,
    }
    action()
  }, [action, selectedNfts, currentOfferByNft])

  if (selectedNfts.length === 0 || !shouldUseChainSwitch) {
    return {
      mutate: () => {},
      acceptCollectionBidAction,
      switchInProgress: false,
    }
  }

  return {
    mutate: wrappedAction,
    acceptCollectionBidAction,
    switchInProgress,
  }
}
